import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, increment, serverTimestamp, writeBatch, Timestamp } from 'firebase/firestore';

/**
 * Optimized shop metrics service that reduces redundant tracking
 * while maintaining all analytics capabilities
 */

/**
 * Get shop metrics for a specific shop
 * @param {string} shopId - Shop ID
 * @returns {Promise<Object>} Shop metrics
 */
export const getShopMetrics = async (shopId) => {
  try {
    // Get the shop document
    const shopDoc = await getDoc(doc(db, 'shops', shopId));
    
    if (!shopDoc.exists()) {
      console.log('No shop found with ID:', shopId);
      return {
        totalStats: {
          imagesProcessed: 0,
          templatesExecuted: 0,
          sizechartsGenerated: 0,
          productsPushed: 0,
          creditsUsed: 0,
          storageSaved: 0
        }
      };
    }

    const shopData = shopDoc.data();
    const metrics = shopData.metrics || {};
    
    // Extract metrics with proper defaults
    const totalImagesProcessed = parseInt(metrics.totalImagesProcessed || 0);
    const totalTemplatesExecuted = parseInt(metrics.templateUsage?.total || 0);
    const totalSizechartsGenerated = parseInt(metrics.sizechartsGenerated || 0);
    const totalProductsPushed = parseInt(metrics.pushes?.total || 0);
    const totalCreditsUsed = parseInt(metrics.creditUsage?.total || 0);
    const totalBytesSavedMB = parseFloat(metrics.totalBytesSavedMB || 0);
    
    // Process monthly data if available
    let monthlyData = [];
    
    // Combine all monthly metrics into a unified format
    const monthlyMetrics = {
      ...metrics.monthlyImageStats,
      ...metrics.templateUsage?.monthly,
      ...metrics.creditUsage?.byMonth,
      ...metrics.pushes?.monthly
    };
    
    // Process monthly data
    Object.entries(monthlyMetrics).forEach(([month, data]) => {
      monthlyData.push({
        month,
        imagesProcessed: parseInt(data.imagesProcessed || 0),
        templatesExecuted: parseInt(data.templates || 0),
        sizechartsGenerated: parseInt(data.sizecharts || 0),
        productsPushed: parseInt(data.pushes || 0),
        creditsUsed: parseInt(data.credits || 0),
        storageSaved: parseFloat(data.bytesSavedMB || 0) * 1024 * 1024 // Convert MB to bytes
      });
    });
    
    // Sort data chronologically
    monthlyData.sort((a, b) => a.month.localeCompare(b.month));
    
    return {
      id: shopId,
      name: shopData.name || shopData.shopName || 'Unknown Shop',
      domain: shopData.domain || shopData.shopDomain || '',
      totalStats: {
        imagesProcessed: totalImagesProcessed,
        templatesExecuted: totalTemplatesExecuted,
        sizechartsGenerated: totalSizechartsGenerated,
        productsPushed: totalProductsPushed,
        creditsUsed: totalCreditsUsed,
        storageSaved: totalBytesSavedMB * 1024 * 1024 // Convert MB to bytes
      },
      monthlyData
    };
  } catch (error) {
    console.error('Error fetching shop metrics:', error);
    throw error;
  }
};

/**
 * Consolidate shop metrics to optimize storage and query performance
 * @returns {Promise<Object>} Result of the consolidation process
 */
export const consolidateShopMetrics = async () => {
  try {
    // Get all shops
    const shopsRef = collection(db, 'shops');
    const shopsSnapshot = await getDocs(shopsRef);
    
    if (shopsSnapshot.empty) {
      return { success: true, message: 'No shops to process', processed: 0 };
    }
    
    let processedCount = 0;
    const batch = writeBatch(db);
    let batchCount = 0;
    const BATCH_LIMIT = 500;
    
    // Process each shop
    for (const shopDoc of shopsSnapshot.docs) {
      const shopData = shopDoc.data();
      const metrics = shopData.metrics || {};
      
      // Skip shops that don't have metrics
      if (!metrics || Object.keys(metrics).length === 0) {
        continue;
      }
      
      // Ensure metrics are properly structured
      const updatedMetrics = {
        ...metrics,
        // Ensure all required fields exist with proper defaults
        totalImagesProcessed: parseInt(metrics.totalImagesProcessed || 0),
        totalBytesSavedMB: parseFloat(metrics.totalBytesSavedMB || 0),
        sizechartsGenerated: parseInt(metrics.sizechartsGenerated || 0),
        lastUpdated: serverTimestamp()
      };
      
      // Ensure template usage structure is correct
      if (!updatedMetrics.templateUsage) {
        updatedMetrics.templateUsage = { total: 0, successful: 0, failed: 0 };
      }
      
      // Ensure credit usage structure is correct
      if (!updatedMetrics.creditUsage) {
        updatedMetrics.creditUsage = { total: 0 };
      }
      
      // Update the shop document
      batch.update(doc(db, 'shops', shopDoc.id), { metrics: updatedMetrics });
      batchCount++;
      processedCount++;
      
      // Commit batch if limit reached
      if (batchCount >= BATCH_LIMIT) {
        await batch.commit();
        console.log(`Committed batch with ${batchCount} shop updates`);
        batchCount = 0;
      }
    }
    
    // Commit any remaining updates
    if (batchCount > 0) {
      await batch.commit();
      console.log(`Committed final batch with ${batchCount} shop updates`);
    }
    
    return {
      success: true,
      message: `Shop metrics consolidated for ${processedCount} shops`,
      processed: processedCount
    };
  } catch (error) {
    console.error('Error consolidating shop metrics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get aggregated metrics for all shops belonging to a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Aggregated metrics for all shops
 */
export const getAllShopsMetrics = async (userId) => {
  try {
    // Query all shops belonging to the user
    const shopsRef = collection(db, 'shops');
    const shopsQuery = query(shopsRef, where('userId', '==', userId));
    const shopsSnapshot = await getDocs(shopsQuery);
    
    if (shopsSnapshot.empty) {
      console.log('No shops found for user');
      return {
        totalStats: {
          imagesProcessed: 0,
          storageSaved: 0,
          compressionRatio: 0,
          sizechartsGenerated: 0,
          creditsUsed: 0
        },
        shopBreakdown: [],
        templateUsage: {
          total: 0,
          successful: 0,
          failed: 0,
          byTemplate: []
        }
      };
    }

    // Initialize aggregated metrics
    let totalImagesProcessed = 0;
    let totalBytesSavedMB = 0;
    let totalTemplateUsage = 0;
    let successfulTemplateUsage = 0;
    let failedTemplateUsage = 0;
    let totalCreditsUsed = 0;
    let totalSizechartsGenerated = 0;
    const templateUsageByTemplate = {};
    const shopBreakdown = [];
    
    // Process each shop
    shopsSnapshot.forEach(shopDoc => {
      const shopData = shopDoc.data();
      const metrics = shopData.metrics || {};
      
      // Image metrics (now considered as product pushes)
      const shopImagesProcessed = parseInt(metrics.totalImagesProcessed || 0);
      const shopBytesSavedMB = parseFloat(metrics.totalBytesSavedMB || 0);
      const shopSizechartsGenerated = parseInt(metrics.sizechartsGenerated || 0);
      
      totalImagesProcessed += shopImagesProcessed;
      totalBytesSavedMB += shopBytesSavedMB;
      totalSizechartsGenerated += shopSizechartsGenerated;
      
      // Credit usage metrics
      const shopCreditsUsed = parseInt(metrics.creditUsage?.total || 0);      
      totalCreditsUsed += shopCreditsUsed;
      
      // Template usage metrics
      const shopTemplateUsage = parseInt(metrics.templateUsage?.total || 0);
      const shopSuccessfulTemplateUsage = parseInt(metrics.templateUsage?.successful || 0);
      const shopFailedTemplateUsage = parseInt(metrics.templateUsage?.failed || 0);
      
      totalTemplateUsage += shopTemplateUsage;
      successfulTemplateUsage += shopSuccessfulTemplateUsage;
      failedTemplateUsage += shopFailedTemplateUsage;
      
      // Process template usage by template
      const byTemplate = metrics.templateUsage?.byTemplate || {};
      Object.entries(byTemplate).forEach(([templateId, usage]) => {
        if (!templateUsageByTemplate[templateId]) {
          templateUsageByTemplate[templateId] = {
            templateId,
            count: 0,
            successful: 0,
            failed: 0
          };
        }
        
        templateUsageByTemplate[templateId].count += parseInt(usage.total || 0);
        templateUsageByTemplate[templateId].successful += parseInt(usage.successful || 0);
        templateUsageByTemplate[templateId].failed += parseInt(usage.failed || 0);
      });
      
      // Add to shop breakdown
      shopBreakdown.push({
        id: shopDoc.id,
        name: shopData.name || shopData.shopName || 'Unknown Shop',
        domain: shopData.domain || shopData.shopDomain || '',
        imagesProcessed: shopImagesProcessed,
        sizechartsGenerated: shopSizechartsGenerated,
        bytesSavedMB: shopBytesSavedMB,
        templateUsage: shopTemplateUsage,
        creditsUsed: shopCreditsUsed
      });
    });
    
    // Calculate average compression ratio (if there are processed images)
    const averageCompressionRatio = totalImagesProcessed > 0 ? 
      // This is an approximation since we don't have original bytes data
      70 : 0;
    
    // Convert template usage by template to array and sort
    const byTemplateArray = Object.values(templateUsageByTemplate)
      .sort((a, b) => b.count - a.count);
    
    // Sort shop breakdown by images processed
    shopBreakdown.sort((a, b) => b.imagesProcessed - a.imagesProcessed);
    
    return {
      totalStats: {
        imagesProcessed: totalImagesProcessed,
        sizechartsGenerated: totalSizechartsGenerated,
        storageSaved: totalBytesSavedMB * 1024 * 1024, // Convert MB to bytes
        compressionRatio: averageCompressionRatio,
        creditsUsed: totalCreditsUsed
      },
      shopBreakdown,
      templateUsage: {
        total: totalTemplateUsage,
        successful: successfulTemplateUsage,
        failed: failedTemplateUsage,
        successRate: totalTemplateUsage > 0 ? 
          ((successfulTemplateUsage / totalTemplateUsage) * 100).toFixed(1) : '0.0',
        byTemplate: byTemplateArray
      },
      creditUsage: {
        total: totalCreditsUsed
      }
    };
  } catch (error) {
    console.error('Error fetching all shops metrics:', error);
    throw error;
  }
};
